import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import {
    HeaderFilterProvider,
    useHeaderFilters,
} from "../../context/HeaderFilterContext";
import useDynamicPageSize from "../../useDynamicPageSize";
import { toast, ToastContainer } from "react-toastify";
import Table from "../../core/common/dataTable";
import { DatePicker } from "antd";
import Footer from "../../Footer";
import dayjs, { Dayjs } from "dayjs";
import nodeApi from "../../nodeApi";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { encryptQuery, decryptQuery } from "../../utils/encryption";
import { useIsMobile } from "../../hooks/useIsMobile";
import { nodeAxiosInstance } from "../../axiosInstance";
import CommonSelect from "../../core/common/commonSelect";

interface CelebrationType {
    id: number;
    title: string | null;
    start_date: string;
    end_date: string;
    media_url?: string;
    media_type?: "image" | "video";
}


type ModalMode = "add" | "edit";

const Banners: React.FC = () => {
    const isMobile = useIsMobile(991);
    const pageSize = useDynamicPageSize();
    const { setFilters } = useHeaderFilters();
    const navigate = useNavigate();
    const location = useLocation();

    const navCausedRef = useRef(false);


    const [data, setData] = useState<CelebrationType[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<string>("active");
    const [tableHeight, setTableHeight] = useState(0);

    const [modalMode, setModalMode] = useState<ModalMode>("add");
    const [editId, setEditId] = useState<number | null>(null);
    const NODE_BASE_URL = nodeAxiosInstance.defaults.baseURL?.replace("/api", "");
    const editingRecord = React.useMemo(() => {
        if (!editId) return null;
        return data.find((d) => d.id === editId) || null;
    }, [editId, data]);
    const [modalReady, setModalReady] = useState(false);
    const TITLE_REGEX = /^[A-Za-z ]+$/;
    const currentYearStart = dayjs().startOf("year");
    const currentYearEnd = dayjs().endOf("year");



    const [formError, setFormError] = useState<{
        title?: string;
        from?: string;
        to?: string;
        media?: string;
    }>({});


    const [formData, setFormData] = useState<{
        title: string;
        from: Dayjs | null;
        to: Dayjs | null;
        media: File | null;
    }>({
        title: "",
        from: null,
        to: null,
        media: null,
    });

    const [preview, setPreview] = useState<{
        open: boolean;
        type?: "image" | "video";
        url?: string;
    }>({
        open: false,
    });

    const isSubmittingRef = useRef(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    // const hasValidPageSize = useRef(false);
    const isInitialFetch = useRef(true);


    /* ================= HEIGHT ================= */

    useLayoutEffect(() => {
        if (pageSize === 0) return; // Prevent setting height too early
        const calculateHeight = () => {
            const baseOffset = 194;
            const extraOffset = window.innerWidth <= 991 ? -43 : -81;
            const fixedOffset = baseOffset + extraOffset;

            const newHeight = window.innerHeight - fixedOffset;

            console.log("Calculated table height:", newHeight);
            console.log("fixedOffset:", fixedOffset);
            console.log("window.innerHeight:", window.innerHeight);
            console.log("totalItems :", totalItems);
            console.log("pagesize :", pageSize);
            setTableHeight(newHeight);
        };
        calculateHeight();
    }, [pageSize]);

    /* ================= FILTER HEADER ================= */

    useEffect(() => {
        const filterOptions = [
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "archived", label: "Archived" },
        ];

        const BannerFilters = () => (
            <div className="upper-absolute-class">
                <div className="Holiday-filters d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex holiday-full-width gap-2">
                        {/* RESET BUTTON */}
                        {selectedFilter !== "active" && (
                            <button
                                className="btn add_btn_size btn-outline-primary reset-holidays"
                                style={{ maxWidth: 200 }}
                                onClick={() => {
                                    setSelectedFilter("active");
                                    setCurrentPage(1);
                                    const encrypted = encryptQuery({
                                        filter: "active",
                                        currentPage: 1,
                                        pageSize,
                                        module: "banners",
                                        ts: Date.now(),
                                    });
                                    navigate(`/banners/${encodeURIComponent(encrypted)}`, { replace: false });
                                }}
                            >
                                <i className="ti ti-refresh" />
                            </button>
                        )}

                        {/* FILTER DROPDOWN */}
                        <div className="holiday-flex">
                            <CommonSelect
                                className="select"
                                options={filterOptions}
                                value={
                                    selectedFilter
                                        ? filterOptions.find((opt) => opt.value === selectedFilter) || null
                                        : null
                                }
                                onChange={(selectedOption) => {
                                    const newFilter = selectedOption?.value || "active";
                                    setSelectedFilter(newFilter);
                                    setCurrentPage(1);

                                    const encrypted = encryptQuery({
                                        filter: newFilter,
                                        currentPage: 1,
                                        pageSize,
                                        module: "banners",
                                        ts: Date.now(),
                                    });

                                    navigate(
                                        `/banners/${encodeURIComponent(encrypted)}`,
                                        { replace: false }
                                    );
                                }}
                                forceFullWidth={isMobile}
                            />
                        </div>

                        {/* ADD BUTTON */}
                        <button
                            className="btn btn-outline-primary add_btn_size holiday-add-margin-right"
                            onClick={() => openModal("add")}
                        >
                            <i className="ti ti-circle-plus" />
                        </button>
                    </div>
                </div>
            </div>
        );

        setFilters(<BannerFilters />);
        return () => setFilters(null);
    }, [selectedFilter, isMobile, pageSize, navigate, setFilters]);


    /* ================= API ================= */

    const fetchCelebrations = async () => {
        try {
            // setLoading(true);

            const res = await nodeApi<any>("/banners/list", {
                method: "POST",
                body: {
                    currentPage,
                    pageSize,
                    filter: selectedFilter,
                },
            });

            setData(Array.isArray(res?.data) ? res.data : []);
            setTotalItems(res?.meta?.total || 0);
        } catch (e: any) {
            toast.error(e?.response?.data?.message ?? e?.message ?? "Something went wrong");
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (navCausedRef.current) {
            navCausedRef.current = false;
            return;
        }

        const [, , encoded] = location.pathname.split("/");

        if (!encoded) {
            setCurrentPage(1);
            setSelectedFilter("active");
            return;
        }

        try {
            const decoded = decryptQuery(decodeURIComponent(encoded));
            if (decoded?.filter) {
                setSelectedFilter(decoded.filter);
            } else {
                setSelectedFilter("active");
            }
            const page = Number(decoded?.currentPage);

            if (!isNaN(page) && page > 0) {
                setCurrentPage(page);
            }
        } catch {
            setCurrentPage(1);
            setSelectedFilter("active");
        }
    }, [location.pathname]);


    useEffect(() => {
        if (!pageSize) return; // wait for dynamic pageSize

        if (isInitialFetch.current) {
            isInitialFetch.current = false;
            return; // SKIP FIRST VALID RUN
        }

        setLoading(true);
        fetchCelebrations();

    }, [currentPage, pageSize, selectedFilter]);




    const handlePageChange = (page: number) => {
        setCurrentPage(page);

        navCausedRef.current = true;

        const encrypted = encryptQuery({
            currentPage: page,
            pageSize,
            filter: selectedFilter,
            module: "banners",
            ts: Date.now(), // optional
        });

        navigate(`/banners/${encodeURIComponent(encrypted)}`, {
            replace: false,
        });
    };




    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const resetForm = () => {
        setModalReady(false);
        setFormData({
            title: "",
            from: null,
            to: null,
            media: null,
        });

        setFormError({});
        setEditId(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    /* ================= MODAL ================= */

    const openModal = (mode: ModalMode, record?: CelebrationType) => {
        //  RESET SUBMIT LOCK
        isSubmittingRef.current = false;

        setPreview({ open: false });

        setFormError({});
        if (fileInputRef.current) fileInputRef.current.value = "";

        if (mode === "add") {
            setEditId(null);
            setModalMode("add");
            setFormData({
                title: "",
                from: null,
                to: null,
                media: null,
            });
        }

        if (mode === "edit" && record) {
            setEditId(record.id);
            setModalMode("edit");
            setFormData({
                title: record.title || "",
                from: record.start_date ? dayjs(record.start_date) : null,
                to: record.end_date ? dayjs(record.end_date) : null,
                media: null,
            });
        }

        setTimeout(() => {
            const modal = document.getElementById("celebration_modal");
            if (modal) {
                (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
            }
        }, 0);
    };





    const hideModal = (modalId: string) => {
        const modal = document.getElementById(modalId);
        if (modal && (window as any).bootstrap?.Modal) {
            const instance = (window as any).bootstrap.Modal.getInstance(modal);
            instance?.hide();
        }
    };

    const openDeleteModal = (id: number) => {
        setDeleteId(id);
        const modal = document.getElementById("top-modal");
        if (modal) {
            (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
        }
    };

    /* ================= SAVE ================= */

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;

        const errors: {
            title?: string;
            from?: string;
            to?: string;
            media?: string;
        } = {}; // Define errors object
        // Validate From Date
        if (!formData.from) {
            errors.from = "From Date is required.";
        }
        if (formData.from && formData.to) {
            if (formData.to.isBefore(formData.from, "day")) {
                errors.to = "To Date cannot be earlier than From Date";
            }
        }


        // MEDIA REQUIRED LOGIC
        const hasExistingMedia =
            modalMode === "edit" && editingRecord?.media_url;

        if (!formData.media && !hasExistingMedia) {
            errors.media = "Image or Video is required.";
        }

        if (Object.keys(errors).length > 0) {
            setFormError(errors); // Set errors in state
            return;
        }

        setFormError({}); // Clear errors if validation passes

        isSubmittingRef.current = true;

        try {
            const body = new FormData();

            // title is OPTIONAL (backend allows null)
            body.append("title", formData.title.trim());

            // From Date is mandatory
            body.append(
                "start_date",
                formData.from!.format("YYYY-MM-DD")
            );

            // To Date optional → unlimited if not provided
            body.append(
                "end_date",
                formData.to ? formData.to.format("YYYY-MM-DD") : ""
            );


            if (formData.media) {
                body.append("media", formData.media);
            }

            if (formData.title && !TITLE_REGEX.test(formData.title)) {
                setFormError({ title: "Only alphabets and spaces are allowed" });
                return;
            }

            const res = await nodeApi<any>(
                modalMode === "add"
                    ? "/banners"
                    : `/banners/${editId}`,
                {
                    method: modalMode === "add" ? "POST" : "PUT",
                    body,
                }
            );

            // res IS the saved object
            if (!res) {
                toast.error("Failed to save Banners");
                return;
            }


            toast.success(
                modalMode === "add"
                    ? "Banner added successfully!"
                    : "Banner updated successfully!"
            );

            hideModal("celebration_modal");
            setCurrentPage(1)
            fetchCelebrations();
        } catch (e: any) {
            toast.error(e?.response?.data?.message ?? e?.message ?? "Something went wrong");
        }

        finally {
            isSubmittingRef.current = false;
        }
    };



    /* ================= DELETE ================= */

    const handleDelete = async () => {
        if (!deleteId) return;

        try {
            const res = await nodeApi<any>(`/banners/${deleteId}`, {
                method: "DELETE",
            });

            if (!res) {
                toast.error("Failed to delete");
                return;
            }


            toast.success("Banner deleted");
            fetchCelebrations();
        } catch (e: any) {
            toast.error(e?.response?.data?.message ?? e?.message ?? "Something went wrong");
        }

    };
    useEffect(() => {
        if (!pageSize) return;

        const totalPages = Math.ceil(totalItems / pageSize);

        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalItems, pageSize, currentPage]);

    /* ================= REORDER ================= */

    const handleReorder = async (id: number, direction: "up" | "down") => {
        try {
            const res = await nodeApi<any>(`/banners/${id}/reorder`, {
                method: "PUT",
                body: { direction },
            });
            if (res) {
                toast.success(res?.message || "Order updated successfully");
                fetchCelebrations();
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message ?? e?.message ?? "Something went wrong");
        }
    };


    /* ================= TABLE ================= */

    const columns = [
        {
            title: "Media",
            key: "media",
            render: (_: any, r: CelebrationType) => {
                if (!r.media_url) {
                    return (
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor: "#f0f0f0",
                            }}
                        />
                    );
                }

                const fullUrl = r.media_url.startsWith("http")
                    ? r.media_url
                    : `${NODE_BASE_URL}${r.media_url}`;

                return (
                    <div
                        onClick={() =>
                            setPreview({
                                open: true,
                                type: r.media_type,
                                url: fullUrl,
                            })
                        }
                        title="Preview Media"
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            overflow: "hidden",
                            cursor: "pointer",
                            position: "relative",
                            backgroundColor: "#000",
                        }}
                    >
                        {r.media_type === "image" ? (
                            <img
                                src={fullUrl}
                                alt="media"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <>
                                {/* video thumbnail fallback */}
                                <video
                                    src={fullUrl}
                                    muted
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                                {/* play icon overlay */}
                                <i
                                    className="ti ti-player-play-filled"
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        color: "#fff",
                                        fontSize: 14,
                                    }}
                                />
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Title",
            key: "title",
            render: (_: any, r: CelebrationType) => {
                const maxTitleLength = 15;

                const truncateText = (text: string, maxLength: number) =>
                    text && text.length > maxLength
                        ? text.substring(0, maxLength) + "..."
                        : text;

                const titleText = r.title || "-";

                return (
                    <div style={{ minWidth: 0 }}>
                        <span
                            title={titleText}          //tooltip behavior
                        >
                            {truncateText(titleText, maxTitleLength)}
                        </span>
                    </div>
                );
            },
        },


        {
            title: "From Date",
            key: "from_date",
            render: (_: any, r: CelebrationType) =>
                dayjs(r.start_date).format("DD-MMM-YYYY"),
        },
        {
            title: "To Date",
            key: "to_date",
            render: (_: any, r: CelebrationType) =>
                r.end_date
                    ? dayjs(r.end_date).format("DD-MMM-YYYY")
                    : "-"
        },
        {
            title: "Actions",
            key: "actions",
            render: (_: any, r: CelebrationType) => {
                const index = data.findIndex((d) => d.id === r.id);
                const isFirst = currentPage === 1 && index === 0;
                const isLast = (currentPage - 1) * pageSize + index + 1 === totalItems;

                return (
                    <div className="action-icon d-inline-flex gap-2 align-items-center">
                        
                        <Link to="#" onClick={() => openModal("edit", r)} title="View">
                            <i className="ti ti-edit" />
                        </Link>
                        <Link to="#" onClick={() => openDeleteModal(r.id)} title="Delete">
                            <i className="ti ti-trash" />
                        </Link>
                        {!isFirst && (
                            <Link to="#" onClick={(e) => { e.preventDefault(); handleReorder(r.id, "up"); }} title="Move Up">
                                <i className="ti ti-arrow-up" />
                            </Link>
                        )}
                        {!isLast && (
                            <Link to="#" onClick={(e) => { e.preventDefault(); handleReorder(r.id, "down"); }} title="Move Down">
                                <i className="ti ti-arrow-down" />
                            </Link>
                        )}
                    </div>
                );
            },
        },
    ];



    /* ================= UI ================= */

    return (
        <>

            <div className="page-wrapper d-flex flex-column">
                <div className="content flex-grow-1">
                    <div className="card card-margin-bottom">
                        <div
                            className="card-body p-0"
                            style={{ height: tableHeight, display: "flex", flexDirection: "column" }}
                        >
                            <Table
                                style={{ flex: 1 }}
                                dataSource={data}
                                columns={columns}
                                loading={loading}
                                currentPage={currentPage}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                onPageChange={handlePageChange}
                                rowClassName={() => "table-row-compact"}
                            />

                        </div>
                    </div>
                </div>

                {/* ADD / EDIT MODAL */}
                <div className="modal fade" id="celebration_modal">
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">
                                    {modalMode === "add" ? "Add Banners" : "Edit Banners"}
                                </h4>
                                <button
                                    className="btn-close"
                                    onClick={() => hideModal("celebration_modal")}
                                />

                            </div>

                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Title
                                    </label>
                                    <input
                                        className={`form-control ${formError.title ? "is-invalid" : ""}`}
                                        value={formData.title}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            // Prevent numbers & special chars
                                            if (value && !TITLE_REGEX.test(value)) {
                                                setFormError((p) => ({
                                                    ...p,
                                                    title: "Only alphabets and spaces are allowed",
                                                }));
                                                return;
                                            }

                                            setFormError((p) => ({ ...p, title: "" }));
                                            setFormData((p) => ({ ...p, title: value }));
                                        }}
                                        onBlur={() => {
                                            if (!formData.title) return;

                                            if (formData.title.trim().length < 3 && !formError.title) {
                                                setFormError((p) => ({
                                                    ...p,
                                                    title: "Title must be at least 3 characters",
                                                }));
                                            }

                                            if (formData.title.trim().length >= 3 && formError.title) {
                                                setFormError((p) => ({ ...p, title: "" }));
                                            }
                                        }}

                                    />

                                    {formError.title && (
                                        <div className="text-danger">{formError.title}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        From Date <span className="text-danger">*</span>
                                    </label>
                                    <DatePicker
                                        className={`form-control ${formError.from ? "is-invalid" : ""}`}
                                        format="DD-MMM-YYYY"
                                        // key={`from-${modalMode}-${editId ?? "new"}`}
                                        value={formData.from ?? null}
                                        allowClear
                                        inputReadOnly={false}
                                        placeholder="DD-MMM-YYYY"
                                        onChange={(d) => {
                                            setFormData((p) => ({ ...p, from: d }));
                                            setFormError((p) => ({ ...p, from: "" }));
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Backspace" || e.key === "Delete") {
                                                setFormData((p) => ({ ...p, from: null }));
                                            }
                                        }}
                                        disabledDate={(current) => {
                                            if (!current) return false;

                                            //  block previous years
                                            if (current.isBefore(currentYearStart, "day")) {
                                                return true;
                                            }

                                            //  block dates after selected To Date (existing logic)
                                            if (formData.to) {
                                                return current.isAfter(formData.to, "day");
                                            }

                                            return false;
                                        }}
                                    />



                                    {formError.from && (
                                        <div className="text-danger">{formError.from}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">To Date</label>
                                    <DatePicker
                                        format="DD-MMM-YYYY"
                                        className="form-control"
                                        key={`to-${modalMode}-${editId ?? "new"}`}
                                        value={formData.to ?? null}
                                        allowClear
                                        inputReadOnly={false}
                                        placeholder="DD-MMM-YYYY"
                                        onChange={(d) =>
                                            setFormData((p) => ({ ...p, to: d }))
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Backspace" || e.key === "Delete") {
                                                setFormData((p) => ({ ...p, to: null }));
                                            }
                                        }}
                                        disabledDate={(c) =>
                                            formData.from ? c.isBefore(formData.from) : false
                                        }
                                    />
                                    {formError.to && (
                                        <div className="text-danger">{formError.to}</div>
                                    )}



                                </div>
                                <div className="mb-3">
                                    <label className="form-label">
                                        Image / Video <span className="text-danger">*</span>
                                    </label>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className={`form-control ${formError.media ? "is-invalid" : ""
                                            }`}
                                        accept="image/*,video/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;

                                            const MAX_VIDEO_SIZE = 5 * 1024 * 1024; // 5MB

                                            if (file.type.startsWith("video/") && file.size > MAX_VIDEO_SIZE) {
                                                toast.error("Video size must be 5MB or less");
                                                return;
                                            }


                                            setFormData((p) => ({ ...p, media: file }));
                                            setFormError((p) => ({ ...p, media: "" }));
                                        }}

                                    />

                                    {formError.media && (
                                        <div className="invalid-feedback d-block">
                                            {formError.media}
                                        </div>
                                    )}
                                </div>

                                {/* MEDIA PREVIEW */}
                                {formData.media ? (
                                    <div className="mt-2">
                                        {formData.media.type.startsWith("image") ? (
                                            <img
                                                src={URL.createObjectURL(formData.media)}
                                                className="celebration-preview"
                                            />
                                        ) : (
                                            <video className="celebration-preview-video" controls>
                                                <source src={URL.createObjectURL(formData.media)} />
                                            </video>
                                        )}
                                    </div>
                                ) : (
                                    modalMode === "edit" && editingRecord?.media_url && !formData.media && (

                                        <div className="mt-2">
                                            {editingRecord.media_type === "image" ? (
                                                <img
                                                    key={editingRecord.media_url} //  FORCE RERENDER
                                                    src={`${NODE_BASE_URL}${editingRecord.media_url}`}
                                                    className="celebration-preview"
                                                />
                                            ) : (
                                                <video
                                                    key={editingRecord.media_url} //  FORCE RERENDER
                                                    className="celebration-preview-video"
                                                    controls
                                                    preload="metadata"
                                                    muted
                                                >
                                                    <source
                                                        src={`${NODE_BASE_URL}${editingRecord.media_url}`}
                                                        type="video/webm"
                                                    />
                                                </video>
                                            )}

                                        </div>
                                    )
                                )}



                            </div>

                            <div className="modal-footer d-flex justify-content-end gap-2">
                                <button className="btn btn-light" onClick={() => hideModal("celebration_modal")}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    {modalMode === "add" ? "Add" : "Update"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* MEDIA PREVIEW MODAL */}
                <div
                    className={`modal fade ${preview.open ? "show d-block" : ""}`}
                    tabIndex={-1}
                    style={{ background: "rgba(0,0,0,0.6)" }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Media Preview</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setPreview({ open: false })}
                                />
                            </div>

                            <div
                                className="modal-body p-0"
                                style={{
                                    maxHeight: "70vh",
                                    overflow: "auto",     // allow scroll if needed
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "center",
                                        padding: "12px",
                                    }}
                                >


                                    {preview.type === "image" && (
                                        <img
                                            src={preview.url}
                                            alt="preview"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "calc(70vh - 24px)", //  subtract padding
                                                width: "auto",
                                                height: "auto",
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                        />

                                    )}

                                    {preview.type === "video" && (
                                        <video
                                            src={preview.url}
                                            controls
                                            autoPlay
                                            preload="metadata"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "calc(70vh - 24px)",
                                                width: "auto",
                                                height: "auto",
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                        />


                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* DELETE CONFIRMATION MODAL */}
                <div id="top-modal" className="modal fade" tabIndex={-1}>
                    <div className="modal-dialog modal-top">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">Delete</h4>
                                <button className="btn-close" data-bs-dismiss="modal" />
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this banner?</p>
                            </div>
                            <div className="modal-footer d-flex justify-content-end gap-2">
                                <button className="btn btn-light" data-bs-dismiss="modal">
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleDelete}
                                    data-bs-dismiss="modal"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>


            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default Banners;
